import { useState, useEffect, useMemo } from "react";
import { isWithinInterval, addHours, isAfter, isBefore } from "date-fns";
import { DateTime } from "luxon";
import { formatInTimeZone } from "date-fns-tz";
import {
  TripLodging,
  ItineraryItem,
  TripTransport,
  Location,
  FlightItem,
  ActivityItem,
} from "@/types";
import useAccommodationStore from "@/stores/tripBuilder/useAccommodation";
import useTripStore from "@/stores/tripBuilder/useTrip";

interface TravelDayItem extends ItineraryItem {
  type: "travel-day";
}

// Utility functions
const processLodging = (
  lodging: TripLodging | null,
  newDay: DateTime,
  newCheckInDate: DateTime,
  newCheckOutDate: DateTime,
  arrivalTransport: TripTransport | undefined,
  locationTimeZone: string
): TripLodging | null => {
  if (!lodging) return null;
  let startTime: string = newCheckInDate.toISO() as string;

  if (newDay.hasSame(newCheckInDate, "day")) {
    if (arrivalTransport && arrivalTransport.legs.length > 0) {
      const lastLeg = arrivalTransport.legs[arrivalTransport.legs.length - 1];
      if (lastLeg) {
        const flightArrivalTime = lastLeg.arrival.time.utc;
        const checkInTime = lodging.policies?.checkIn || "";

        if (isAfter(new Date(flightArrivalTime), new Date(checkInTime))) {
          startTime = formatInTimeZone(
            addHours(new Date(flightArrivalTime), 1),
            "UTC",
            "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'"
          );
        }
      }
    }

    return {
      ...lodging,
      name: `Check-in: ${lodging.name}`,
      start_time: startTime,
    };
  } else if (newDay.hasSame(newCheckOutDate, "day")) {
    return {
      ...lodging,
      name: `Check-out: ${lodging.name}`,
      start_time: lodging.policies?.checkOut || "",
    };
  } else {
    const dayStart = DateTime.fromISO(newDay.toISO() as string)
      .setZone(locationTimeZone)
      .startOf("day");

    const isWithinStay =
      dayStart >= DateTime.fromISO(lodging.policies?.checkIn || "") &&
      dayStart < DateTime.fromISO(lodging.policies?.checkOut || "");

    if (!isWithinStay) {
      return null;
    }

    return {
      ...lodging,
      name: `Staying at: ${lodging.name}`,
      start_time: dayStart.toUTC().toISO() as string,
    };
  }
};

const processFlights = (
  transport: TripTransport[],
  selectedLocationId: string
): FlightItem[] => {
  return transport
    .map((transport) => {
      const isArriving = transport.toLocationId === selectedLocationId;
      // Ensure we have a valid leg
      if (transport.legs.length === 0) {
        return null;
      }

      const departureLeg = transport.legs[0];
      const arrivalLeg = transport.legs[transport.legs.length - 1];

      const leg = isArriving ? arrivalLeg : departureLeg;

      if (!leg || !departureLeg || !arrivalLeg) {
        return null;
      }

      const name = isArriving
        ? `Arriving into ${leg.arrival.location.name}`
        : `Departing from ${leg.departure.location.name}`;

      // Create a ServerFlightInfo compatible object
      const flightInfo: FlightItem = {
        id: transport.id,
        departureLocation: {
          ...departureLeg.departure.location,
        },
        arrivalLocation: {
          ...arrivalLeg.arrival.location,
        },
        departureTime: leg.departure.time.utc,
        arrivalTime: leg.arrival.time.utc,
        duration: leg.duration,
        airline: [
          {
            name: leg.provider.name || "",
            code: leg.provider.code || "",
            logo: leg.provider.logo || "",
          },
        ],
        coordinates: isArriving
          ? leg.arrival.location.coordinates
          : leg.departure.location.coordinates,
        price: {
          amount: leg.booking_info?.price || 0,
          currency: leg.booking_info?.currency || "USD",
        },
        name: name,
        start_time: isArriving ? leg.arrival.time.utc : leg.departure.time.utc,
        isArriving: isArriving,
      };

      return flightInfo;
    })
    .filter((flight): flight is FlightItem => flight !== null);
};

export const useSelectedDayItems = (
  selectedDay: string,
  selectedLocationId: string | undefined
): ActivityItem[] => {
  const [selectedDayItems, setSelectedDayItems] = useState<ActivityItem[]>([]);
  const { getMainLocationLodging } = useAccommodationStore();
  const { tripData } = useTripStore();
  const selectedDayUtc = useMemo(
    () => DateTime.fromISO(selectedDay, { zone: "utc" }),
    [selectedDay]
  );

  const allItems = useMemo(() => {
    if (!selectedDay || !tripData || !selectedLocationId) {
      return [];
    }

    const selectedLocation = tripData.locations.find(
      (location) => location.id === selectedLocationId
    );

    if (!selectedLocation) {
      return [];
    }

    // find transport to selected location
    const transportToSelectedLocation = tripData.transport.filter(
      (transport) => transport.toLocationId === selectedLocationId
    );

    const arrivalTransport = transportToSelectedLocation[0];
    const arrivalTime =
      arrivalTransport?.legs[arrivalTransport.legs.length - 1]?.arrival.time
        .utc;

    let travelDayItem: TravelDayItem | null = null;
    if (
      arrivalTime &&
      DateTime.fromISO(arrivalTime)
        .setZone(selectedLocation.date.time_zone!)
        .startOf("day") > selectedDayUtc.startOf("day")
    ) {
      travelDayItem = getTravelDayItem(
        selectedDayUtc,
        selectedLocation,
        arrivalTransport
      );
    }

    const currentLocation = tripData.locations.find(
      (loc) => loc.id === selectedLocationId
    );

    const itineraryItems =
      currentLocation?.itinerary_items?.find(
        (item: any) =>
          item.date &&
          selectedDayUtc.hasSame(
            DateTime.fromISO(item.date, { zone: "utc" }),
            "day"
          )
      )?.items || [];

    const locationLodging: TripLodging | null =
      getMainLocationLodging(selectedLocationId);

    let relevantLodging: TripLodging | null = null;
    let processedLodging: TripLodging | null = null;

    if (!locationLodging) {
      relevantLodging = null;
    } else {
      relevantLodging = isLodgingRelevant(
        locationLodging,
        selectedDayUtc,
        selectedLocation
      )
        ? locationLodging
        : null;
    }

    if (!relevantLodging) {
      relevantLodging = null;
    } else {
      processedLodging = processLodging(
        relevantLodging,
        selectedDayUtc,
        DateTime.fromISO(relevantLodging.policies?.checkIn || "").setZone(
          selectedLocation.date.time_zone!
        ),
        DateTime.fromISO(relevantLodging.policies?.checkOut || "").setZone(
          selectedLocation.date.time_zone!
        ),
        findArrivalTransport(
          tripData.transport,
          selectedDayUtc,
          selectedLocationId
        ),
        selectedLocation.date.time_zone!
      );
    }

    let relevantTransport: TripTransport[] = [];
    relevantTransport = tripData.transport.filter((transport) =>
      isTransportRelevant(transport, selectedDayUtc, selectedLocation)
    );

    const processedFlights = processFlights(
      relevantTransport,
      selectedLocationId
    );

    return [
      ...itineraryItems,
      ...(processedLodging ? [processedLodging] : []),
      ...processedFlights,
      ...(travelDayItem ? [travelDayItem] : []),
    ];
  }, [
    selectedDay,
    tripData,
    selectedLocationId,
    selectedDayUtc,
    getMainLocationLodging,
  ]);

  useEffect(() => {
    const sortedItems = allItems.sort((a, b) => {
      if (a === null || b === null) {
        return 0;
      }

      const getTimeWithTimezone = (
        item: ItineraryItem | TripLodging | FlightItem | TravelDayItem,
        locationTimeZone: string
      ): number => {
        if (!item.start_time) return 0;

        // Convert to DateTime with proper timezone
        const dateTime = DateTime.fromISO(item.start_time, {
          zone: locationTimeZone,
        });

        return dateTime.valueOf();
      };

      // Get the selected location's timezone
      const selectedLocation = tripData?.locations.find(
        (location) => location.id === selectedLocationId
      );
      const locationTimeZone = selectedLocation?.date.time_zone || "UTC";

      const aTime = getTimeWithTimezone(a, locationTimeZone);
      const bTime = getTimeWithTimezone(b, locationTimeZone);
      const comparison = aTime - bTime;

      return comparison;
    });

    const transformedItems = sortedItems
      .filter(
        (
          item
        ): item is ItineraryItem | TripLodging | FlightItem | TravelDayItem =>
          item !== null
      )
      .map((item): ActivityItem => {
        if ("type" in item && item.type === "travel-day") {
          return {
            ...item,
            activityType: "travel-day",
          };
        }
        if ("policies" in item) {
          return {
            ...item,
            activityType: "accommodation",
          };
        }
        if ("isArriving" in item) {
          return {
            ...item,
            activityType: "flight",
          };
        }
        // Must be an ItineraryItem
        return {
          ...item,
          activityType: "experience",
        };
      });

    setSelectedDayItems(transformedItems);
  }, [allItems, tripData, selectedLocationId]);

  return selectedDayItems;
};

// Helper functions
const isLodgingRelevant = (
  lodging: TripLodging,
  selectedDayUtc: DateTime,
  location: Location
): boolean => {
  const lodgingStart = DateTime.fromISO(lodging.policies?.checkIn || "", {
    zone: location.date.time_zone!,
  }).toFormat("yyyy-MM-dd");
  const lodgingEnd = DateTime.fromISO(lodging.policies?.checkOut || "", {
    zone: location.date.time_zone!,
  }).toFormat("yyyy-MM-dd");
  return (
    isWithinInterval(selectedDayUtc.toJSDate(), {
      start: new Date(lodgingStart),
      end: new Date(lodgingEnd),
    }) ||
    selectedDayUtc.hasSame(DateTime.fromISO(lodgingStart), "day") ||
    selectedDayUtc.hasSame(DateTime.fromISO(lodgingEnd), "day")
  );
};

const isTransportRelevant = (
  transport: TripTransport,
  selectedDayUtc: DateTime,
  selectedLocation: Location
): boolean => {
  if (transport.legs.length === 0) return false;

  const firstLeg = transport.legs[0];
  const lastLeg = transport.legs[transport.legs.length - 1];

  if (!firstLeg || !lastLeg || !selectedLocation) return false;

  const departureDate = DateTime.fromISO(firstLeg.departure.time.utc, {
    zone: selectedLocation.date.time_zone,
  });
  const arrivalDate = DateTime.fromISO(lastLeg.arrival.time.utc, {
    zone: selectedLocation.date.time_zone,
  });

  return (
    (departureDate.hasSame(selectedDayUtc, "day") &&
      transport.fromLocationId === selectedLocation.id) ||
    (arrivalDate.hasSame(selectedDayUtc, "day") &&
      transport.toLocationId === selectedLocation.id)
  );
};

const findArrivalTransport = (
  transport: TripTransport[],
  selectedDayUtc: DateTime,
  selectedLocationId: string
): TripTransport | undefined => {
  return transport.find((t) => {
    if (t.legs.length === 0) return false;
    const lastLeg = t.legs[t.legs.length - 1];
    if (!lastLeg) return false;

    return (
      t.toLocationId === selectedLocationId &&
      selectedDayUtc.hasSame(
        DateTime.fromISO(lastLeg.arrival.time.utc, {
          zone: "utc",
        }),
        "day"
      )
    );
  });
};

const getTravelDayItem = (
  selectedDayUtc: DateTime,
  location: Location,
  arrivalTransport?: TripTransport
): TravelDayItem | null => {
  if (!arrivalTransport || arrivalTransport.legs.length === 0) return null;

  const lastLeg = arrivalTransport.legs[arrivalTransport.legs.length - 1];
  if (!lastLeg) return null;

  const locationStart = DateTime.fromISO(location.date.start_date)
    .setZone("utc")
    .startOf("day");
  const arrivalDate = DateTime.fromISO(lastLeg.arrival.time.utc)
    .setZone(location.date.time_zone)
    .startOf("day");

  // Check if selected day is between location start and arrival
  // Check if selected day is between location start and arrival
  const isBeforeArrival =
    !selectedDayUtc.hasSame(arrivalDate, "day") && selectedDayUtc < arrivalDate;
  const isAfterLocationStart =
    !selectedDayUtc.hasSame(locationStart, "day") &&
    selectedDayUtc > locationStart;
  const isDuringTravel = isBeforeArrival;

  if (!isDuringTravel) return null;
  if (!isDuringTravel) return null;

  return {
    id: `travel-day-${selectedDayUtc.toISODate()}`,
    sourceId: {
      travelDayId: selectedDayUtc.toISODate()!,
    },
    name: "Travel Day",
    start_time: selectedDayUtc.startOf("day").toISO(),
    duration: null,
    images: [],
    coordinates: location.coordinates,
    metadata: {
      description:
        "This is a travel day before your arrival at the destination.",
    },
    pricing: 0,
    type: "travel-day",
    bookingStatus: "booked",
    participants: null,
  };
};
